import { useRef, useEffect, useCallback, type RefCallback } from 'react';

interface CursorGlowOptions {
    /** Glow radius in px (default: 200) */
    size?: number;
    /** Max opacity when hovering, 0-1 (default: 0.35) */
    opacity?: number;
    /** Glow color — use rgba for best results (default: "rgba(14,76,232,0.6)") */
    color?: string;
    /** Blur amount in px (default: 40) */
    blur?: number;
    /** Skip attaching listeners (default: false) */
    disabled?: boolean;
}

/**
 * Generic hook that creates a cursor-following glow effect via CSS custom properties.
 *
 * Sets `--glow-x`, `--glow-y`, `--glow-opacity`, `--glow-color`, `--glow-size`,
 * and `--glow-blur` on the referenced element. All updates go directly to the DOM
 * (no React re-renders).
 *
 * @example
 * ```tsx
 * const glowRef = useCursorGlow()
 *
 * <div ref={glowRef} className="relative">
 *     <div
 *         aria-hidden="true"
 *         className="pointer-events-none absolute inset-0 rounded-[inherit] transition-opacity duration-300"
 *         style={{
 *             background:
 *                 'radial-gradient(circle var(--glow-size) at var(--glow-x) var(--glow-y), var(--glow-color), transparent)',
 *             opacity: 'var(--glow-opacity)',
 *             filter: 'blur(var(--glow-blur))',
 *         }}
 *     />
 *     {children}
 * </div>
 * ```
 */
export function useCursorGlow<T extends HTMLElement = HTMLDivElement>(
    options: CursorGlowOptions = {},
): RefCallback<T> {
    const {
        size = 55,
        opacity = 1,
        color = 'rgba(0, 72, 255, 0.42)',
        blur = 20,
        disabled = false,
    } = options;

    const elRef = useRef<T | null>(null);
    const handlersRef = useRef<{
        move: (e: MouseEvent) => void;
        leave: () => void;
    } | null>(null);

    useEffect(() => {
        const el = elRef.current;
        if (!el || disabled) return;

        // Set static custom properties once
        el.style.setProperty('--glow-color', color);
        el.style.setProperty('--glow-size', `${size}px`);
        el.style.setProperty('--glow-blur', `${blur}px`);
        el.style.setProperty('--glow-opacity', '0');

        const handleMouseMove = (e: MouseEvent) => {
            const rect = el.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            el.style.setProperty('--glow-x', `${x}px`);
            el.style.setProperty('--glow-y', `${y}px`);
            el.style.setProperty('--glow-opacity', String(opacity));
        };

        const handleMouseLeave = () => {
            el.style.setProperty('--glow-opacity', '0');
        };

        el.addEventListener('mousemove', handleMouseMove);
        el.addEventListener('mouseleave', handleMouseLeave);

        handlersRef.current = {
            move: handleMouseMove,
            leave: handleMouseLeave,
        };

        return () => {
            el.removeEventListener('mousemove', handleMouseMove);
            el.removeEventListener('mouseleave', handleMouseLeave);
            handlersRef.current = null;
        };
    }, [size, opacity, color, blur, disabled]);

    const ref = useCallback((node: T | null) => {
        // Cleanup previous element
        if (elRef.current && handlersRef.current) {
            elRef.current.removeEventListener('mousemove', handlersRef.current.move);
            elRef.current.removeEventListener('mouseleave', handlersRef.current.leave);
        }
        elRef.current = node;
    }, []);

    return ref;
}
